import React, { useState, useEffect, useCallback } from 'react';
import { MdSchool, MdClass, MdPerson, MdCalendarToday, MdArrowForward, MdRefresh } from 'react-icons/md';
import { useClass } from '../utils/ClassContext';

const Dashboard = ({ username, userRole, apiBaseUrl, onNavigate, userName, onClassSelect, isSelectingClassRef }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { changeClass } = useClass();

  // Fetch user's classes
  const fetchUserClasses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/user/${username}/classes`, {
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      } else {
        throw new Error('Failed to fetch classes');
      }
    } catch (err) {
      setError(err.message);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, username, userRole]);

  // Handle class selection
  const handleClassSelect = (classData) => {
    // Set protection flag BEFORE calling changeClass to prevent sync useEffect from clearing
    if (isSelectingClassRef) {
      console.log('[Dashboard] 🛡️ Setting class selection protection flag');
      isSelectingClassRef.current = true;
    }

    changeClass(classData);

    // Defer navigation to next tick to ensure React processes the changeClass() state update
    // This prevents a race condition where handleNavigation checks shouldRedirectToDashboard
    // before the context has updated, causing it to redirect back to dashboard
    setTimeout(() => {
      // Close main sidebar to save space
      if (onClassSelect) {
        onClassSelect();
      }

      onNavigate('home'); // Navigate to class-specific home

      // Clear protection flag after navigation completes
      if (isSelectingClassRef) {
        setTimeout(() => {
          console.log('[Dashboard] ⏰ Clearing class selection protection flag');
          isSelectingClassRef.current = false;
        }, 500);
      }
    }, 0);
  };

  // Handle instructor "All Classes" option
  const handleAllClassesSelect = () => {
    // Set protection flag BEFORE calling changeClass to prevent sync useEffect from clearing
    if (isSelectingClassRef) {
      console.log('[Dashboard] 🛡️ Setting class selection protection flag (All Classes)');
      isSelectingClassRef.current = true;
    }

    changeClass({ class_id: 'all', class_name: 'All Classes', class_code: 'ALL' });

    // Defer navigation to next tick to ensure React processes the changeClass() state update
    setTimeout(() => {
      // Close main sidebar to save space
      if (onClassSelect) {
        onClassSelect();
      }

      onNavigate('home');

      // Clear protection flag after navigation completes
      if (isSelectingClassRef) {
        setTimeout(() => {
          console.log('[Dashboard] ⏰ Clearing class selection protection flag (All Classes)');
          isSelectingClassRef.current = false;
        }, 500);
      }
    }, 0);
  };

  // Load classes on component mount
  useEffect(() => {
    fetchUserClasses();
  }, [fetchUserClasses]);

  // Get time-based greeting
  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="min-h-[72px] flex items-center">
            <div className="flex items-center justify-between w-full">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {getTimeGreeting()}, {userName?.firstName || username}!
                </h1>
                <p className="text-gray-600 mt-1">
                  Select a class to get started with your work
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MdPerson className="text-lg" />
                <span className="capitalize">{userRole}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error State */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <div className="text-red-600">
                <span className="font-medium">Error loading classes:</span> {error}
              </div>
              <button
                onClick={fetchUserClasses}
                className="ml-auto text-red-600 hover:text-red-800 transition-colors"
              >
                <MdRefresh className="text-lg" />
              </button>
            </div>
          </div>
        )}

        {/* Classes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* All Classes Option (Instructors only) */}
          {userRole === 'instructor' && (
            <div
              onClick={handleAllClassesSelect}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MdClass className="text-blue-600 text-xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">All Classes</h3>
                      <p className="text-sm text-gray-500">View across all classes</p>
                    </div>
                  </div>
                  <MdArrowForward className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>
                <div className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1 inline-block">
                  Instructor View
                </div>
              </div>
            </div>
          )}

          {/* Individual Classes */}
          {classes.map((cls) => (
            <div
              key={cls.class_id}
              onClick={() => handleClassSelect(cls)}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MdSchool className="text-blue-600 text-xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {cls.class_name || 'Unknown Class'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {cls.class_code || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <MdArrowForward className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MdCalendarToday className="text-gray-400" />
                    <span>{cls.semester || 'N/A'} {cls.year || 'N/A'}</span>
                  </div>
                  
                  {cls.instructor_name && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MdPerson className="text-gray-400" />
                      <span>{cls.instructor_name}</span>
                    </div>
                  )}
                </div>

                {cls.description && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {cls.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {classes.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <MdSchool className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No classes found</h3>
            <p className="text-gray-500 mb-4">
              You're not enrolled in any active classes yet.
            </p>
            <button
              onClick={fetchUserClasses}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <MdRefresh />
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;